import { FC, ReactNode, useMemo, useRef, useState } from "react";
import { Product, ProductCurrencyPrice } from "../../../interfaces/Product";
import { useDataTableStore } from "../../../stores/DataTableStore";
import { AddProduct } from "../../../components/Products/AddProduct";
import { UpdateProduct } from "../../../components/Products/UpdateProduct";
import { DeleteProduct } from "../../../components/Products/DeleteProduct";
import { v4 as uuidv4 } from 'uuid';
import { UserModel } from "../../modules/auth/models/UserModel";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "../../../setup";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useIntl } from "react-intl";
import { currencyFormatter } from "../../../functions/general";
import { DataTableImport } from "../../../components/DataTable/DataTableImport";
import { ExpandableElement } from "../../../components/General/ExpandableElement";
import moment from "moment";
import useModalEffect from "../../../hooks/useModalEffect";
import useDynamicTab from "../../../hooks/useDynamicTab";
import { useCurrencyStore } from "../../../stores/CurrencyStore";
import { MasterDataTableTanstack } from "../../../components/DataTable/MasterDataTableTanstack";
import { CellContext, ColumnDef, Row } from "@tanstack/react-table";
import { mutate } from "swr";
import { API_URL } from "../../../api/SettingCRUD";
import { CurrencyCodes } from "../../../interfaces/Currency";
import LabelTooltip from "../../../components/General/LabelTooltip";

const today = new Date()
const tableKey = 'products'
const table_URL = 'products'

const MasterProducts: FC = () => {
    const intl = useIntl()
    const user: UserModel = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel
    const { multiCurrencies } = useCurrencyStore()
    const { tablesData, setTableColumnVisibility, tableColumnVisibility, setTableColumns, tableColumns, setIsLoadingTable, isFullyLoaded, selectedFilters, filteredTablesData } = useDataTableStore()

    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    const [product, setProduct] = useState<Product>();
    const [sorting, setSorting] = useState([])
    const [sortByCurrency, setSortByCurrency] = useState<string | null>(null)

    // const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [openMenuId2, setOpenMenuId2] = useState<string | null>(null);
    const anchorEl = useRef<{ [key: string]: HTMLElement | null }>({});
    const anchorRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

    const onHeadClicked = async (head: any, event: React.MouseEvent<any>) => {
        event.stopPropagation()
        setSorting(head.sortBy)
        setSortByCurrency(null)
    }

    // const columns: ColumnDef<Product>[] = [
    const columns = useMemo<ColumnDef<Product>[]>(() => [
        {
            id: "actions",
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }),
            meta: {
                // size: 1,
                // maxSize: 1,
                styleHeader: {
                    alignContent: 'center',
                    textAlign: 'center',
                },
                styleBody: {
                    alignContent: 'center',
                    textAlign: 'center',
                    width: '30px',
                    overflow: 'auto',
                },
                isPinnedLeft: true,
                disableRowFn: true,
            },
            cell: (props: CellContext<Product, unknown>) => {
                const row = props.row.original

                const handleClick = (menuId: string) => {
                    setOpenMenuId(menuId);
                };
                const handleClose = () => {
                    setOpenMenuId(null);
                };
                return (
                    <>
                        {(row.creator_id === user.data.id ||
                            user.data.is_superadmin ||
                            user.data.permission_product_update !== 'NONE' ||
                            user.data.permission_product_delete !== 'NONE') && (
                                <>
                                    <IconButton
                                        aria-label={`more-${row.id}`}
                                        id={`menu-anchor-${row.id}`}
                                        onClick={() => handleClick(row.id)}
                                        ref={(el) => { anchorRefs.current[row.id] = el }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        id={`menu-${row.id}`}
                                        anchorEl={anchorRefs.current[row.id]}
                                        open={openMenuId === row.id}
                                        onClose={handleClose}
                                        disableScrollLock={true}
                                    >
                                        {(row.creator_id === user.data.id ||
                                            user.data.is_superadmin ||
                                            user.data.permission_product_update !== 'NONE') && (
                                                <MenuItem
                                                    onClick={() => {
                                                        handleClose()
                                                        setShowUpdateModal(true)
                                                        setProduct(row)
                                                    }}
                                                >
                                                    <i className='fas fa-edit fs-4 me-4'></i>
                                                    <span>
                                                        {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                                                    </span>
                                                </MenuItem>
                                            )}
                                        {(row.creator_id === user.data.id ||
                                            user.data.is_superadmin ||
                                            user.data.permission_product_delete !== 'NONE') && (
                                                <MenuItem
                                                    onClick={() => {
                                                        handleClose()
                                                        setShowDeleteModal(true)
                                                        setProduct(row)
                                                    }}
                                                >
                                                    <i className='fas fa-trash fs-3 me-5'></i>
                                                    <span>
                                                        {intl.formatMessage({ id: 'FORM.ACTION.DELETE' })}
                                                    </span>
                                                </MenuItem>
                                            )}
                                    </Menu>
                                </>
                            )}
                    </>
                )
            }
        },
        {
            id: uuidv4(),
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            meta: {
                styleHeader: {
                    width: '200px',
                },
                styleBody: {
                    width: '200px',
                },
            },
            accessorKey: 'name',
            cell: (props: CellContext<Product, unknown>) => {
                const row = props.row.original
                var labels = row?.productLabels?.map((label: any) => ({ text: label.label.name, color: label.label.color })) || [];

                return (
                    <div className="d-flex flex-row gap-2">
                        <div>
                            {row.name}
                        </div>
                        <div>
                            {
                                labels.length > 0 && (
                                    <LabelTooltip data={labels}>
                                        {labels}
                                    </LabelTooltip>
                                )
                            }
                        </div>
                    </div>
                )
            }
        },
        ...(multiCurrencies && multiCurrencies.length > 0
            ? [
                {
                    id: 'price',
                    header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRICE' }),
                    meta: {
                        styleHeader: {
                            textAlign: 'center',
                        },
                    },
                    columns: multiCurrencies.map((currency, index) => ({
                        header: currency.code,
                        meta: {
                            styleHeader: {
                                textAlign: 'left',
                            },
                            styleBody: {
                                width: '140px',
                                textAlign: 'right',
                            },
                        },
                        cell: (props: CellContext<Product, unknown>) => {
                            const row = props.row.original
                            const getPriceCurrency = row.prices.find((p: ProductCurrencyPrice) => p.currencyCode === currency.code && p.variantPriceId === null);
                            return typeof getPriceCurrency === 'object' && (getPriceCurrency?.price || getPriceCurrency?.price === 0) ? currencyFormatter(Number(getPriceCurrency.price), currency.isRounded, currency.code as CurrencyCodes, { showPrefix: true }) : '-'
                        },
                        accessorFn: (row: Product) => {
                            const priceObj = row.prices.find(p => (p.currencyCode === currency.code && p.variantPriceId === null));
                            const price = priceObj?.price ?? '-'; // Ubah ini jadi string '-' jika tidak ada price
                            return `${price} ∪ ${currencyFormatter(Number(price || 0), currency.isRounded, currency.code as CurrencyCodes, { showPrefix: true })} ∪ ${priceObj?.currencyName ?? ''} ∪ ${priceObj?.currencySymbol ?? ''} ∪ ${priceObj?.currencyCode ?? ''}`;
                        },
                        sortingFn: (rowA: Row<Product>, rowB: Row<Product>, columnId: string) => {
                            const getRawValue = (row: Row<Product>) => {
                                const raw = String(row.getValue(columnId)).split(' ∪ ')[0];
                                return raw === '-' ? -Infinity : Number(raw); // Anggap '-' sebagai -Infinity
                            };

                            const aRaw = getRawValue(rowA);
                            const bRaw = getRawValue(rowB);

                            return aRaw - bRaw;
                        }
                    })),
                }
            ]
            : []
        ),
        {
            id: uuidv4(),
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.VAT' }),
            meta: {
                styleHeader: {
                    width: '40px',
                },
                styleBody: {
                    width: '40px',
                },
            },
            cell: (props: CellContext<Product, unknown>) => {
                const row = props.row.original
                return <div style={{
                    background: row.isUsingVat ? '#2e8ae3' : 'gray',
                    color: 'white',
                    fontSize: '11px',
                    padding: '2px 6px',
                    textAlign: 'center',
                    textWrap: 'nowrap',
                    fontWeight: '500',
                    borderRadius: '5px'
                }}>
                    {intl.formatMessage({ id: row.isUsingVat ? 'FORM.LABEL.ACTIVE' : 'FORM.LABEL.NOT_ACTIVE' })}
                </div>
            },
            accessorKey: 'vat',
        },
        {
            id: uuidv4(),
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.UNIT' }),
            meta: {
                styleHeader: {
                    width: '200px',
                },
                styleBody: {
                    width: '200px',
                },
            },
            cell: (props: CellContext<Product, unknown>) => {
                const row = props.row.original
                const units = row.productUnits.map(
                    (data) => `${currencyFormatter(data.convertionToPieces)} PCS/${data.unit.name}`
                )

                return units.length > 0 ? units.join(', ') : 'PCS'
                // return row.vatPercentage ? `${row.vatPercentage} %` : row.vatPercentage === 0 ? '0 %' : '-'
            },
            accessorFn: (row) => {
                const units = row.productUnits.map(
                    (data) => `${currencyFormatter(data.convertionToPieces)} PCS/${data.unit.name}`
                )

                return units.length > 0 ? units.join(', ') : 'PCS'
            },
        },
        {
            id: uuidv4(),
            header: 'SKU',
            meta: {
                styleHeader: {
                    width: '200px',
                },
                styleBody: {
                    width: '200px',
                },
            },
            cell: (props: CellContext<Product, unknown>) => {
                const row = props.row.original
                return row.sku ? row.sku : '-'
            },
            accessorKey: 'sku',
        },
        {
            id: uuidv4(),
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_STATUS' }),
            meta: {
                styleHeader: {
                    width: '40px',
                },
                styleBody: {
                    width: '40px',
                },
            },
            cell: (props: CellContext<Product, unknown>) => {
                const row = props.row.original
                return <div style={{
                    background: row.isCanAccess ? '#2e8ae3' : 'gray',
                    color: 'white',
                    fontSize: '11px',
                    padding: '2px 6px',
                    textAlign: 'center',
                    textWrap: 'nowrap',
                    fontWeight: '500',
                    borderRadius: '5px'
                }}>
                    {intl.formatMessage({ id: row.isCanAccess ? 'FORM.LABEL.ACTIVE' : 'FORM.LABEL.NOT_ACTIVE' })}
                </div>
            },
            accessorKey: 'isCanAccess',
        },
        {
            id: uuidv4(),
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
            meta: {
                styleHeader: {
                    width: '200px',
                },
                styleBody: {
                    width: '200px',
                },
            },
            cell: (props: CellContext<Product, unknown>) => {
                const row = props.row.original
                if (row.note) {
                    var data = row.note.replaceAll('\n', '<br/>')
                    return <ExpandableElement text={data} maxHeight={100} />
                } else return '-'
            },
            accessorKey: 'note',
        },
        {
            id: uuidv4(),
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.COLOR' }),
            meta: {
                styleHeader: {
                    textAlign: 'center',
                },
            },
            cell: (props: CellContext<Product, unknown>) => {
                const row = props.row.original
                return (
                    <div
                        style={{
                            width: '20px',
                            height: '20px',
                            margin: 'auto',
                            borderRadius: '6px',
                            backgroundColor: row.color ?? '#D33115',
                        }}
                    />
                )
            },
            accessorKey: 'color',
            enableGlobalFilter: false,
        },
        {
            id: 'dateCreated',
            accessorKey: 'dateCreated',
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_CREATED' }),
            accessorFn: (row) => {
                return (
                    moment(row.dateCreated).format("DD MMM YYYY")
                )
            },
            // sortable: true,
        },
        // ];
    ], [intl, multiCurrencies, openMenuId, sortByCurrency, openMenuId2]);

    let actions: ReactNode[] = []
    if (user.data.permission_product_create !== "NONE" || user.data.permission_product_update !== "NONE") {
        actions.push(<DataTableImport source={"template-product"} />)
    }
    if (user.data.permission_product_create !== 'NONE') {
        actions.push(<AddProduct />)
    }

    //   actions.push(
    //     <FilterProduct 
    //       tableKey={tableKey} 
    //       manageColumns={manageColumns} 
    //       setManageColumns={setManageColumns}
    //       // onVisibilityChange={handleVisibilityChange}
    //     />
    //   )

    useModalEffect({
        showModal: showUpdateModal,
        setShowModal: setShowUpdateModal,
        modalId: `update-product-modal-${product?.id}`
    })

    useModalEffect({
        showModal: showDeleteModal,
        setShowModal: setShowDeleteModal,
        modalId: `delete-product-modal-${product?.id}`
    })

    useDynamicTab({ title: `${intl.formatMessage({ id: 'MENU.PRODUCTS' })} | CRM WooBlazz` });

    return (
        <>
            <div className="row justify-content-between mb-3">
                <div className='col-sm-3 col-lg-4 col-xl-5' style={{ alignSelf: 'center' }}>
                    <ol className='breadcrumb text-muted fs-6 fw-bold'>
                        <li className="breadcrumb-item text-dark">{intl.formatMessage({ id: 'MENU.PRODUCTS' })}</li>
                    </ol>
                </div>

                <div className="col-lg-8 col-sm-9 col-xl-7">
                    <div className="d-flex float-end px-3">
                        {
                            isFullyLoaded ?
                                <div
                                    onClick={() => {
                                        mutate(`${API_URL}${table_URL}`)
                                        // setFetchCounter(fetchCounter + 1)
                                    }}
                                    className="btn btn-light btn-sm ms-2"
                                >
                                    <i className="p-0 fa-sync-alt fas"></i>
                                </div> :
                                <div className="ms-2 my-auto spinner-border spinner-border-sm" role="status">
                                </div>
                        }
                    </div>
                </div>
            </div>
            <MasterDataTableTanstack
                tableKey={tableKey}
                tableColumns={columns}
                actions={actions}
                apiURL={table_URL}
                // dateStart={`${date.getFullYear()}-${date.getMonth() + 1}-01`}
                // dateEnd={moment(date).format("YYYY-MM-DD")}
                autoReload={false}
                onHeadClicked={(head, e) => onHeadClicked(head, e)}
                tableStyle={{
                    size: 'MEDIUM',
                    fontSize: '12px',
                }}
                orderBy='dateCreated'
                orderType='DESC'
            />
            {showUpdateModal && <UpdateProduct product={product!} />}
            {showDeleteModal && <DeleteProduct product={product!} />}
        </>
    )
}

export { MasterProducts }